<template>
    <draggable
        v-bind="dragOptions"
        tag="div"
        class="category-tree-level"
        :list="items"
        @input="emitter"
        @start="isDragging = true"
        @end="isDragging = false"
        @change="onDragChange"
    >
        <div
            v-for="category in items"
            :key="category.categoryId"
            class="category-tree-item"
        >
            <div class="category-content">
                <div
                    v-if="category.children && category.children.length > 0"
                    class="tree-toggle"
                    @click.stop="toggleCategory(category)"
                >
                    <i class="fas" :class="category.expanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                </div>
                <div v-else class="tree-toggle-spacer"></div>

                <div
                    class="category-block"
                    :class="['m' + category.color]"
                    @click="$emit('edit-category', category.categoryId)"
                >
                    <div class="category-block-left">
                        <span class="category-name">{{ category.name }}</span>
                    </div>
                    <div class="category-block-right">
                        <div class="category-item-counts">
                            <span>{{ $t('category.itemsCount') }} <strong>{{ (category.primaryItems || 0) + (category.secondaryItems || 0) }}</strong></span>
                        </div>
                    </div>
                </div>
            </div>

            <category-tree-item
                v-if="initChildren(category)"
                v-show="category.expanded || isAnyDragging"
                class="category-children"
                :value="category.children"
                :parent-dragging="isAnyDragging"
                @input="updateChildren(category, $event)"
                @edit-category="$emit('edit-category', $event)"
                @change="onNestedDragChange(category, $event)"
            />
        </div>
    </draggable>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    name: 'CategoryTreeItem',
    components: {
        draggable
    },
    props: {
        value: {
            required: false,
            type: Array,
            default: null
        },
        // `list` prop is used by vuedraggable when nested
        list: {
            required: false,
            type: Array,
            default: null
        },
        // Propagate dragging state from parent
        parentDragging: {
            required: false,
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isDragging: false,
            dragOptions: {
                animation: 200,
                group: 'categories',
                disabled: false,
                ghostClass: 'ghost-category',
                fallbackOnBody: true,
                swapThreshold: 0.65
            }
        };
    },
    computed: {
        // Use `list` if available (from vuedraggable), otherwise use `value` (from v-model)
        items() {
            return this.list || this.value || [];
        },
        // Combine local and parent dragging state
        isAnyDragging() {
            return this.isDragging || this.parentDragging;
        }
    },
    watch: {
        items: {
            handler: function(newItems) {
                // Auto-expand categories that just got children
                newItems.forEach(cat => {
                    if (cat.children && cat.children.length > 0) {
                        if (cat.expanded === undefined) {
                            this.$set(cat, 'expanded', true);
                        }
                    }
                });
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        // When the top-level list is modified, emit `input` for v-model
        emitter(value) {
            this.$emit('input', value);
        },
        // When a nested list is modified, we need to update the parent's children array
        updateChildren(parentCategory, newChildren) {
            this.$set(parentCategory, 'children', newChildren);
        },
        toggleCategory(category) {
            // Mutate the `expanded` property directly, ensuring reactivity with $set
            this.$set(category, 'expanded', !category.expanded);
        },
        initChildren(category) {
            if (!category.children) {
                this.$set(category, 'children', []);
            }
            // Auto-expand when children array just got its first item (only if undefined, not false)
            if (category.children && category.children.length > 0 && category.expanded === undefined) {
                this.$set(category, 'expanded', true);
            }
            return true; // Always return true so drag-drop zone is always available
        },
        onDragChange(evt) {
            // Propagate change event up
            this.$emit('change', evt);
        },
        onNestedDragChange(parentCategory, evt) {
            // When an item is added to nested children, auto-expand the parent
            if (evt.added) {
                this.$nextTick(() => {
                    if (parentCategory.children && parentCategory.children.length > 0) {
                        this.$set(parentCategory, 'expanded', true);
                    }
                });
            }
            // Propagate event further up
            this.$emit('change', evt);
        }
    }
};
</script>

<style scoped>
/* --- NEW, ROBUST SPACING LOGIC --- */

/* 1. Space between sibling items */
.category-tree-level {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* 2. Space between a parent's content and its children list */
.category-children {
    margin-top: 8px;
    margin-left: 55px; /* Indentation for nesting */
}


/* --- ORIGINAL STYLES FOR LOOK & FEEL (UNCHANGED) --- */

.category-content {
    display: flex;
    align-items: center;
    min-height: 50px;
}

.tree-toggle {
    cursor: pointer;
    color: #fff;
    font-size: 16px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    transition: all 0.2s ease;
    margin-right: 8px;
    flex-shrink: 0;
    min-height: 46px;
    min-width: 46px;
    max-height: 46px;
    max-width: 46px;
}

.tree-toggle:hover {
    background-color: rgba(0, 0, 0, 0.4);
}

.tree-toggle-spacer {
    min-width: 52px; /* 46px toggle + 8px margin */
    flex-shrink: 0;
}

.ghost-category {
    opacity: 0.5;
    background: #f0f8ff;
}

.category-block {
    flex: 1;
    padding: 0px 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 51px;
}

.category-block-left {
    /* No specific styles needed, takes natural width */
}

.category-block-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.category-name {
    color: #333;
    padding: 12px;
    font-weight: 600;
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.category-item-counts {
    color: #333;
    font-size: 14px;
    background-color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    margin: 2px;
}

.category-item-counts span {
    display: block;
}

.category-block.m1 { background-color: #8ea459; }
.category-block.m2 { background-color: #7ac4dd; }
.category-block.m3 { background-color: #7aa1ec; }
.category-block.m4 { background-color: #f0e071; }
.category-block.m5 { background-color: #e49d5b; }
.category-block.m6 { background-color: #b191dc; }
.category-block.m7 { background-color: #d9736f; }
.category-block.m8 { background-color: #ceb19f; }

.category-block:hover {
    transform: translateY(-3px);
    box-shadow: 0px 4px 8px hsl(0, 0%, 0%);
    outline: 1px solid rgba(0, 0, 0, 0.5);
}</style>